import { clamp } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

import { CrossIcon } from "@/components/Icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useDebounce from "@/hooks/useDebounce";
import { CommentatorT, useSearchCommentators } from "@/hooks/useSearchCommentators";
import { PlayerT, useSearchPlayers } from "@/hooks/useSearchPlayers";
import { cx } from "@/utils/cx";
import { generateImageUrl } from "@/utils/image";

/* eslint-disable @typescript-eslint/no-explicit-any */
export const SearchInput = <T extends Record<string, any>>({
    value,
    onValueChange,
    label,
    className,
    placeholder = "Search",
    suggestions,
    displayField,
    idField,
    query,
    onQueryChange,
}: {
    value?: T | null;
    onValueChange: (value: T | null) => void;
    label?: string;
    className?: string;
    placeholder?: string;
    suggestions: T[];
    displayField: keyof T;
    idField: keyof T;
    query: string;
    onQueryChange: (query: string) => void;
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [hasContainerFocus, setHasContainerFocus] = useState(false);
    const [hasFocus, setHasFocus] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [highlightedId, setHighlightedId] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!(hasFocus || hasContainerFocus)) {
            setHighlightedId(undefined);
            return;
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                const currentIndex = suggestions.findIndex(item => item[idField] === highlightedId);
                const nextIndex = clamp(
                    currentIndex + (event.key === "ArrowDown" ? 1 : -1),
                    -1,
                    suggestions.length - 1
                );
                if (nextIndex === -1) {
                    setHighlightedId(undefined);
                } else if (suggestions[nextIndex]) {
                    setHighlightedId(suggestions[nextIndex][idField]);
                }

                if (scrollRef.current) {
                    const item = scrollRef.current.children[nextIndex];
                    if (item) {
                        scrollRef.current.scrollTo({ top: item.scrollHeight * nextIndex });
                    }
                }
            } else if (event.key === "Enter" && highlightedId) {
                const item = suggestions.find(item => item[idField] === highlightedId);
                if (item) {
                    onValueChange(item);
                    onQueryChange("");
                    setHighlightedId(undefined);
                }
            } else if (event.key === "Escape") {
                onQueryChange("");
                setHighlightedId(undefined);
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [
        hasContainerFocus,
        hasFocus,
        highlightedId,
        idField,
        onQueryChange,
        onValueChange,
        suggestions,
    ]);

    return (
        <div className={cx("flex flex-col gap-2", className)}>
            {label && <Label>{label}</Label>}
            {value ? (
                <div className="flex">
                    <div
                        key={value[idField]}
                        className="m-1 flex items-center space-x-2 rounded bg-violet-200 px-3 py-1 shadow"
                    >
                        {value[displayField]}
                        <button onClick={() => onValueChange(null)}>
                            <CrossIcon />
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex">
                    <div
                        className="relative"
                        ref={containerRef}
                        onClick={() => setHasContainerFocus(true)}
                    >
                        <Input
                            ref={inputRef}
                            value={query}
                            onChange={event => {
                                onQueryChange(event.target.value);
                                setHighlightedId(undefined);
                            }}
                            onFocus={() => {
                                setHasFocus(true);
                                setHasContainerFocus(true);
                            }}
                            onBlur={() => setHasFocus(false)}
                            className="w-full max-w-full"
                            placeholder={placeholder}
                        />
                        <div
                            ref={scrollRef}
                            className={cx(
                                "absolute left-0 right-0 z-20 max-h-80 overflow-scroll bg-white shadow-md",
                                (!(hasFocus || hasContainerFocus) || !query) && "hidden"
                            )}
                        >
                            <div className="p-2 text-sm text-gray-500">
                                Use ↑ / ↓ to browse
                                {highlightedId && (
                                    <>
                                        <br />⏎ to select{" "}
                                        <span className="font-semibold text-black">
                                            {
                                                suggestions.find(
                                                    item => item[idField] === highlightedId
                                                )?.[displayField]
                                            }
                                        </span>
                                    </>
                                )}
                            </div>
                            {suggestions.map(item => (
                                <div
                                    key={item[idField]}
                                    onClick={() => {
                                        onValueChange(item);
                                        onQueryChange("");
                                        setHighlightedId(undefined);
                                    }}
                                    className={cx(
                                        "cursor-pointer p-2 hover:bg-gray-200",
                                        highlightedId === item[idField] && "bg-gray-300"
                                    )}
                                >
                                    {item[displayField]}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export const SearchCommentatorInput = ({
    commentator,
    onCommentatorChange,
    ...props
}: {
    commentator?: CommentatorT | null;
    onCommentatorChange: (commentator: CommentatorT | null) => void;
    label?: string;
    className?: string;
}) => {
    const [query, setQuery] = useState("");

    const debouncedQuery = useDebounce(query, 500);

    const { commentators } = useSearchCommentators({
        query: debouncedQuery,
        currentPage: 0,
        pause: !debouncedQuery,
    });
    return (
        <SearchInput<CommentatorT>
            value={commentator}
            onValueChange={onCommentatorChange}
            suggestions={commentators}
            displayField="displayName"
            idField="id"
            query={query}
            onQueryChange={setQuery}
            {...props}
        />
    );
};

export const SearchAndSelectPlayersInput = ({
    selectedPlayers,
    onSelectedPlayersChange,
    className,
    label,
}: {
    selectedPlayers: PlayerT[];
    onSelectedPlayersChange: (value: PlayerT[]) => void;
    className?: string;
    label?: string;
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [hasContainerFocus, setHasContainerFocus] = useState(false);
    const [hasFocus, setHasFocus] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [highlightedId, setHighlightedId] = useState<string | undefined>(undefined);

    const [query, setQuery] = useState("");

    const debouncedQuery = useDebounce(query, 500);

    const { players, loading } = useSearchPlayers({
        query: debouncedQuery,
        currentPage: 0,
        pause: !debouncedQuery,
    });

    const addPlayer = useCallback(
        (player: PlayerT) => {
            const existing = selectedPlayers.find(v => v.id === player.id);

            setQuery("");
            setHighlightedId(undefined);

            if (!existing) {
                onSelectedPlayersChange([...selectedPlayers, player]);
            }
        },
        [onSelectedPlayersChange, selectedPlayers]
    );

    useEffect(() => {
        if (!(hasFocus || hasContainerFocus)) {
            setHighlightedId(undefined);
            return;
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                const currentIndex = players.findIndex(item => item.id === highlightedId);
                const nextIndex = clamp(
                    currentIndex + (event.key === "ArrowDown" ? 1 : -1),
                    -1,
                    players.length - 1
                );
                if (nextIndex === -1) {
                    setHighlightedId(undefined);
                } else if (players[nextIndex]) {
                    setHighlightedId(players[nextIndex].id);
                }

                if (scrollRef.current) {
                    const item = scrollRef.current.children[nextIndex];
                    if (item) {
                        scrollRef.current.scrollTo({ top: item.scrollHeight * nextIndex });
                    }
                }
            } else if (event.key === "Enter") {
                if (highlightedId) {
                    const suggestedPlayer = players.find(item => item.id === highlightedId);
                    if (suggestedPlayer) {
                        addPlayer(suggestedPlayer);
                    }
                }
            } else if (event.key === "Escape") {
                setQuery("");
                setHighlightedId(undefined);
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [addPlayer, hasContainerFocus, hasFocus, highlightedId, players]);

    return (
        <div className={cx("flex flex-col gap-2", className)}>
            {label && <Label>{label}</Label>}
            <div className="gap-12">
                <div className="flex-1">
                    <div
                        className="relative"
                        ref={containerRef}
                        onClick={() => setHasContainerFocus(true)}
                    >
                        <Input
                            ref={inputRef}
                            value={query}
                            onChange={event => {
                                setQuery(event.target.value);
                                setHighlightedId(undefined);
                            }}
                            onFocus={() => {
                                setHasFocus(true);
                                setHasContainerFocus(true);
                            }}
                            onBlur={() => setHasFocus(false)}
                            className="w-full max-w-full"
                            placeholder={"Search players"}
                        />
                        <div
                            className={cx(
                                "absolute left-0 right-0 top-14 z-20 max-h-80 bg-white shadow-md",
                                (!(hasFocus || hasContainerFocus) || !query) && "hidden"
                            )}
                        >
                            <div className="p-2 text-sm text-gray-500">
                                {loading ? "Loading..." : "Use ↑ / ↓ to browse"}
                                {highlightedId && (
                                    <>
                                        <br />⏎ to select{" "}
                                        <span className="font-semibold text-black">
                                            {players.find(item => item.id === highlightedId)?.name}
                                        </span>
                                    </>
                                )}
                            </div>
                            <div ref={scrollRef} className={"max-h-80 overflow-y-scroll bg-white"}>
                                {players.map(item => (
                                    <div
                                        key={item.id}
                                        onClick={() => {
                                            const existing = selectedPlayers.find(
                                                v => v.id === item.id
                                            );

                                            setQuery("");
                                            setHighlightedId(undefined);

                                            if (!existing) {
                                                onSelectedPlayersChange([...selectedPlayers, item]);
                                            }
                                        }}
                                        className={cx(
                                            "flex cursor-pointer items-center gap-2 p-2 hover:bg-gray-200",
                                            highlightedId === item.id && "bg-gray-300"
                                        )}
                                    >
                                        {item.imageId ? (
                                            <PlayerImage imageId={item.imageId} />
                                        ) : null}
                                        <span>{item.name}</span>
                                        <span className="text-xs">({item.fideId})</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex-row">
                    <span className="px-2 text-sm">
                        Selected players count: {selectedPlayers.length}
                    </span>
                    <div className="flex flex-row flex-wrap">
                        {selectedPlayers.map(p => (
                            <div
                                key={p.id}
                                className="m-1 flex items-center gap-2 rounded bg-violet-200 px-3 py-1 shadow"
                            >
                                <PlayerImage imageId={p.imageId} />
                                <span>{p.name}</span>
                                <span className="text-xs">({p.fideId})</span>
                                <button
                                    onClick={() => {
                                        onSelectedPlayersChange(
                                            selectedPlayers.filter(v => v.id !== p.id)
                                        );
                                    }}
                                >
                                    <CrossIcon />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const SearchSinglePlayerInput = ({
    player,
    onPlayerChange,
    label,
    className,
}: {
    player?: PlayerT | null;
    onPlayerChange: (player: PlayerT | null) => void;
    label?: string;
    className?: string;
}) => {
    const [query, setQuery] = useState("");
    const debouncedQuery = useDebounce(query, 500);

    const { players } = useSearchPlayers({
        query: debouncedQuery,
        currentPage: 0,
        pause: !debouncedQuery,
    });

    return (
        <SearchInput<PlayerT>
            value={player ?? null}
            onValueChange={onPlayerChange}
            label={label}
            className={className}
            placeholder="Search players"
            suggestions={players}
            displayField="name"
            idField="id"
            query={query}
            onQueryChange={setQuery}
        />
    );
};

const PlayerImage = ({ imageId }: { imageId: string | null }) => {
    if (!imageId) {
        return null;
    }

    return (
        <img
            src={generateImageUrl({
                imageId,
                width: 50,
                height: 50,
                fit: "clip",
            })}
            alt="avatar"
            className="image-input-wrapper opacity-75-hover h-5 cursor-pointer"
        />
    );
};
